<template>
  <div @click="selectedCask = null">
    <breadcrumbs home="Empty/reduce contents of cask(s)"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>
          <v-row>
            <v-col cols="6" sm="4" lg="3">
              <date-picker v-model="transaction.date" @change="refresh"/>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="8" lg="6">
              <outlined-block title="Casks to empty" :disabled="!edit" v-model="transaction.casks" :rules="[casksCheck]">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="transaction.casks" item-key="key"
                              no-data-text="No casks specified"
                              :headers="[{text:'',value:'add',width:'15%',align:'center',cellClass:'pa-0',divider:true},
                                        {text:'Number',value:'number',width:'25%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                                        {text:'Contents',value:'alcoholName',width:'60%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'}]" class="no-sort"
                              :item-class="item => item.key=== selectedCask ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedCask = item.key; $nextTick(() => this.$refs.form.validate())}">
                  <template v-slot:item.add="{item,index}">
                    <v-btn class="mt-n2" icon @click.stop="deleteCask(index)" x-small :disabled="!edit"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.number="{item}">
                    <template v-if="selectedCask === item.key">
                      <v-text-field v-model="item.number" dense outlined label="Cask #" :rules="[fieldRules.required,fieldRules.maxLength(10)]"
                                    @change="lookupCask(item)"/>
                    </template>
                    <div v-else class="ml-1" :class="{'error--text':!item.id}">
                      {{item.number ? item.number : 'Required'}}
                      <div class="text-caption" v-if="item.number && item.error">{{item.error}}</div>
                    </div>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedCask = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn :disabled="!edit" fab color="primary" x-small @click.stop="addCask"><v-icon small>mdi-plus</v-icon></v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6" sm="3" lg="2">
              <alcohol-input label="Total volume" :spirit="true"
                             v-model="transaction.volume" :litres.sync="transaction.litres" :disabled="!edit"
                            :kg.sync="transaction.kg" :strength.sync="transaction.strength"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6" lg="4">
              <tank-select v-model="transaction.tankId" :tanks="tanks" label="Put contents in tank" outlined :hint="transactionId===undefined"
                          :highlight="tank => transactionId === undefined && transaction.casks.length && tank.contents.alcoholId === transaction.casks[0].alcoholId"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="8" lg="5">
              <v-checkbox label="Record remaining book LAA of casks(s) as Angel's Share" class="mt-1" v-model="transaction.angelsShare"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" counter="255"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="transactionId && company.permissions.includes('inventory.write')">
          <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import TankSelect from '@/components/TankSelect'

export default {
  name: 'InventoryCasksEmpty',
  components: {TankSelect},
  mixins: [confirmLeave],
	props: {
    casks: {type:Array,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      edit: false,
      valid:true,
      saving:false,
      tanks: [],
      selectedCask: null,
      transaction: {date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), casks: [],
                    volume: 0, litres: 0, kg: 0, strength: 0, comments:'', tankId:'',angelsShare:false},
      dirty: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    }
  },
  watch: {
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    if (this.transactionId) {
      const results = await this.$apiCall(`query InventoryCasksEmpty($id:ID!) {
                                              casksEmpty(id:$id) {
                                                id,casks{id,number,alcoholId,alcoholName},date,volume,litres,kg,strength,comments,tankId,angelsShare
                                              }
                                            }`,
                                          {id:this.transactionId});
      if (results.error) {
        this.$nextTick(() => this.dirty = false);
        this.$router.back();
        return;
      }
      this.transaction = results.data.casksEmpty;
    }
    else {
      this.transaction.casks = [...this.casks.map(cask => ({id:cask.id, number:cask.number, alcoholId:cask.contents.alcoholId, alcoholName:cask.contents.alcoholName}))];
      this.$nextTick(() => this.$refs.form.validate());
      this.edit = true;
    }
    let key = Date.now();
    this.transaction.casks.forEach(cask => cask.key = key++);

    await this.refresh();
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    casksCheck() {
      if (!this.transaction.casks.length)
        return 'Cask(s) must be specified';
      let unique = new Set(this.transaction.casks.map(cask => cask.alcoholId));
      if (unique.size > 1)
        return 'All casks must have the same contents';
      unique = new Set(this.transaction.casks.map(cask => cask.number));
      if (unique.size !== this.transaction.casks.length)
        return 'There are duplicate casks. Casks can only be added once';
      return true;
    },
    async addCask() {
      const cask = {id:null, key:Date.now(), number:'', alcoholId:'', alcoholName:''};
      this.transaction.casks.push(cask);
      this.selectedCask = cask.key;
    },
    async lookupCask(item) {
      item.error = null;
      item.id = null;
      const results = await this.$apiCall(`query InventoryCasksEmpty($filters: [FilterInput]) {
                                              casks2(first:1,filters:$filters) {
                                                edges {
                                                  node {
                                                    id,number,contents {alcoholId, alcoholName}
                                                  }
                                                }
                                              }
                                            }`,
                                          {filters:[{field:'number', comparator:'eq', value:item.number}]});
      if (!results.error) {
        if (results.data.casks2.edges.length) {
          const cask = results.data.casks2.edges[0].node;
          Object.assign(item, {id:cask.id, number:cask.number, alcoholId:cask.contents.alcoholId, alcoholName:cask.contents.alcoholName});
          this.$nextTick(() => this.$refs.form.validate());
        }
        else {
          item.error = 'Not found';
          item.contents = {};
        }
      }
    },
    deleteCask(index) {
      this.transaction.casks.splice(index, 1);
    },
    async refresh() {
      this.tanks = [];
      await this.$lazyListLoad(this.tanks, 'tanks2', `id,name,tare,site{id},contents{volume,alcoholType,alcoholName,alcoholId}`, 100, [{field:'active', comparator:'eq', value:'true'}], [{name:'asof', value:this.transaction.date}]);
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      if (this.valid && !this.transaction.casks.some(cask => !cask.id)) {
        this.saving = true;
        const {casks, ...input} = this.transaction;
        input.casks = this.transaction.casks.map(cask => cask.id);
        const results = await this.$apiCall(`mutation InventoryCasksEmpty($input: CasksEmptyInput!) {
                                                casksEmptyCreate(input:$input) {id}
                                              }`,
                                            {input});
        this.saving = false;
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} empty cask(s) transaction`});
        }
      }
      else
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
		}
	}
}
</script>